import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { alert } from "@/contexts/AlertModalContext";
import { AlertCircle, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { MfaCodeDialog, MfaEnrollDialog } from "@/components/mfa";
import { MfaRequiredError } from "@/lib/mfaError";

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"form">) {
  const { t } = useTranslation();
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [capsLock, setCapsLock] = useState(false);
  // MFA
  const [mfaCodeOpen, setMfaCodeOpen] = useState(false);
  const [mfaInvalid, setMfaInvalid] = useState(false);
  const [mfaEnrollOpen, setMfaEnrollOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const realizarLogin = async (mfaToken?: string) => {
    setCarregando(true);
    setErro(null);
    try {
      const usuarioLogado = await login(usuario, senha, "BO", mfaToken);
      setMfaCodeOpen(false);
      setMfaInvalid(false);
      // Se ainda não tem MFA, recomenda ativar antes de entrar.
      if (usuarioLogado.mfa_enabled) {
        alert.success(t("login.success"));
        navigate("/", { replace: true });
      } else {
        setMfaEnrollOpen(true);
      }
    } catch (error) {
      if (error instanceof MfaRequiredError) {
        setMfaInvalid(error.invalid);
        setMfaCodeOpen(true);
      } else {
        console.error("Erro ao fazer login:", error);
        setErro(t("login.invalidCredentials"));
      }
    } finally {
      setCarregando(false);
    }
  };


  const detectCapsLock = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeof e.getModifierState === "function") {
      setCapsLock(e.getModifierState("CapsLock"));
    }
  };

  return (
    <>
    <form
      className={cn("flex flex-col gap-6", className)}
      {...props}
      onSubmit={async (e) => {
        e.preventDefault();
        await realizarLogin();
      }}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold tracking-tight">
          {t("login.welcome")}
        </h1>
        <p className="text-muted-foreground text-sm text-balance">
          {t("login.subtitle")}
        </p>
      </div>
      <div className="grid gap-6">
        <div className="grid gap-3">
          <Label htmlFor="email">{t("login.userOrEmail")}</Label>
          <Input
            id="email"
            type="text"
            autoComplete="username"
            autoFocus
            placeholder="seu@email.com"
            required
            value={usuario}
            onChange={(e) => {
              setUsuario(e.target.value);
              if (erro) setErro(null);
            }}
          />
        </div>
        <div className="grid gap-2">
          <div className="flex items-center">
            <Label htmlFor="password">{t("login.password")}</Label>
            <a
              href="https://www.inovacode.app.br/login"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-sm underline-offset-4 hover:underline select-none"
            >
              {t("login.forgotPassword")}
            </a>
          </div>
          <div className="relative">
            <Input
              autoComplete="current-password"
              id="password"
              type={mostrarSenha ? "text" : "password"}
              required
              value={senha}
              onChange={(e) => {
                setSenha(e.target.value);
                if (erro) setErro(null);
              }}
              onKeyDown={detectCapsLock}
              onKeyUp={detectCapsLock}
              onBlur={() => setCapsLock(false)}
              className="pr-10"
            />
            <button
              type="button"
              className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setMostrarSenha(!mostrarSenha)}
              tabIndex={-1}
              aria-label={
                mostrarSenha ? t("login.hidePassword") : t("login.showPassword")
              }
              aria-pressed={mostrarSenha}
            >
              {mostrarSenha ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {capsLock && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <KeyRound className="h-3 w-3" />
              {t("login.capsLock")}
            </p>
          )}
        </div>

        {erro && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive dark:text-red-400"
          >
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{erro}</span>
          </div>
        )}

        <Button type="submit" className="w-full" disabled={carregando}>
          {carregando ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("login.signingIn")}
            </>
          ) : (
            t("login.signIn")
          )}
        </Button>
      </div>
      <div className="text-center text-sm select-none">
        {t("login.noAccount")}{" "}
        <a
          href="https://www.inovacode.app.br/register"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 font-medium"
        >
          {t("login.signUp")}
        </a>
      </div>
    </form>

      <MfaCodeDialog
        open={mfaCodeOpen}
        invalid={mfaInvalid}
        loading={carregando}
        onSubmit={(code) => realizarLogin(code)}
        onCancel={() => {
          setMfaCodeOpen(false);
          setMfaInvalid(false);
        }}
      />

      <MfaEnrollDialog
        open={mfaEnrollOpen}
        onConfigure={() => {
          setMfaEnrollOpen(false);
          window.open(
            "https://www.inovacode.app.br/account/security",
            "_blank",
          );
          navigate("/", { replace: true });
        }}
        onDismiss={() => {
          setMfaEnrollOpen(false);
          navigate("/", { replace: true });
        }}
      />
    </>
  );
}
